import React, { useState, useEffect } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import { pagesService } from '../../../lib/services';

const SitemapGenerator = () => {
  const [pages, setPages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [generating, setGenerating] = useState(false);
  const [sitemapXml, setSitemapXml] = useState('');
  const [lastGenerated, setLastGenerated] = useState(null);
  const [showPreview, setShowPreview] = useState(false);
  const [copied, setCopied] = useState(false);
  const [pageSettings, setPageSettings] = useState({});

  const priorityOptions = ['1.0', '0.9', '0.8', '0.7', '0.6', '0.5', '0.4', '0.3', '0.2', '0.1'];
  const changefreqOptions = ['always', 'hourly', 'daily', 'weekly', 'monthly', 'yearly', 'never'];

  useEffect(() => {
    loadPages();
  }, []);

  const loadPages = async () => {
    setLoading(true);
    const { data } = await pagesService?.getAll({ status: 'published' });
    const publishedPages = data || [];
    setPages(publishedPages);
    const defaults = {};
    publishedPages?.forEach(p => {
      defaults[p.id] = {
        included: true,
        priority: p?.pageType === 'home' ? '1.0' : '0.8',
        changefreq: 'weekly'
      };
    });
    setPageSettings(defaults);
    setLoading(false);
  };

  const generateSitemap = async () => {
    setGenerating(true);
    const siteUrl = window.location?.origin;
    const now = new Date()?.toISOString()?.split('T')?.[0];

    const includedPages = pages?.filter(p => pageSettings?.[p?.id]?.included !== false);

    const urlEntries = includedPages?.map(page => {
      const settings = pageSettings?.[page?.id] || {};
      const slug = page?.slug === '/' || page?.slug === 'home' ? '' : `/${page?.slug}`;
      const lastmod = page?.updatedAt ? new Date(page.updatedAt)?.toISOString()?.split('T')?.[0] : now;
      return `  <url>
    <loc>${siteUrl}${slug}</loc>
    <lastmod>${lastmod}</lastmod>
    <changefreq>${settings?.changefreq || 'weekly'}</changefreq>
    <priority>${settings?.priority || '0.8'}</priority>
  </url>`;
    })?.join('\n');

    const xml = `<?xml version="1.0" encoding="UTF-8"?>
<urlset xmlns="http://www.sitemaps.org/schemas/sitemap/0.9"
        xmlns:xsi="http://www.w3.org/2001/XMLSchema-instance"
        xsi:schemaLocation="http://www.sitemaps.org/schemas/sitemap/0.9
        http://www.sitemaps.org/schemas/sitemap/0.9/sitemap.xsd">
${urlEntries}
</urlset>`;

    setSitemapXml(xml);
    setLastGenerated(new Date());
    setShowPreview(true);
    setGenerating(false);
  };

  const handleCopy = () => {
    navigator.clipboard?.writeText(sitemapXml);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  const handleDownload = () => {
    const blob = new Blob([sitemapXml], { type: 'application/xml' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'sitemap.xml';
    a?.click();
    URL.revokeObjectURL(url);
  };

  const togglePageIncluded = (pageId) => {
    setPageSettings(prev => ({
      ...prev,
      [pageId]: { ...prev?.[pageId], included: !prev?.[pageId]?.included }
    }));
  };

  const updatePageSetting = (pageId, key, value) => {
    setPageSettings(prev => ({
      ...prev,
      [pageId]: { ...prev?.[pageId], [key]: value }
    }));
  };

  const includedCount = pages?.filter(p => pageSettings?.[p?.id]?.included !== false)?.length;

  return (
    <div className="bg-white rounded-lg border border-gray-200 p-4 md:p-6">
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-lg bg-green-100 flex items-center justify-center">
            <Icon name="Map" size={20} className="text-green-600" />
          </div>
          <div>
            <h2 className="text-lg md:text-xl font-semibold text-gray-900">Sitemap Generator</h2>
            <p className="text-sm text-gray-600">Auto-generate sitemap.xml from your published pages</p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          {lastGenerated && (
            <span className="text-xs text-gray-500 hidden md:block">
              Last generated: {lastGenerated?.toLocaleTimeString()}
            </span>
          )}
          <Button
            variant="primary"
            iconName="RefreshCw"
            iconPosition="left"
            onClick={generateSitemap}
            disabled={generating || loading}
          >
            {generating ? 'Generating...' : 'Generate Sitemap'}
          </Button>
        </div>
      </div>
      {/* Stats Row */}
      <div className="grid grid-cols-3 gap-4 mb-6">
        <div className="bg-green-50 rounded-lg p-3 text-center">
          <div className="text-2xl font-bold text-green-700">{pages?.length}</div>
          <div className="text-xs text-green-600 mt-1">Published Pages</div>
        </div>
        <div className="bg-blue-50 rounded-lg p-3 text-center">
          <div className="text-2xl font-bold text-blue-700">{includedCount}</div>
          <div className="text-xs text-blue-600 mt-1">Included in Sitemap</div>
        </div>
        <div className="bg-purple-50 rounded-lg p-3 text-center">
          <div className="text-2xl font-bold text-purple-700">{pages?.length - includedCount}</div>
          <div className="text-xs text-purple-600 mt-1">Excluded</div>
        </div>
      </div>
      {/* Page List */}
      {loading ? (
        <div className="flex items-center justify-center py-8">
          <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-green-600"></div>
          <span className="ml-2 text-sm text-gray-500">Loading pages...</span>
        </div>
      ) : pages?.length === 0 ? (
        <div className="text-center py-8 bg-gray-50 rounded-lg">
          <Icon name="FileX" size={32} className="text-gray-400 mx-auto mb-2" />
          <p className="text-sm text-gray-500">No published pages found. Publish pages to include them in your sitemap.</p>
        </div>
      ) : (
        <div className="space-y-2 mb-6">
          <div className="flex items-center justify-between mb-3">
            <h3 className="text-sm font-semibold text-gray-700">Page Configuration</h3>
            <div className="flex gap-2">
              <button
                onClick={() => {
                  const all = {};
                  pages?.forEach(p => { all[p.id] = { ...pageSettings?.[p?.id], included: true }; });
                  setPageSettings(all);
                }}
                className="text-xs text-blue-600 hover:text-blue-800"
              >
                Select All
              </button>
              <span className="text-gray-300">|</span>
              <button
                onClick={() => {
                  const none = {};
                  pages?.forEach(p => { none[p.id] = { ...pageSettings?.[p?.id], included: false }; });
                  setPageSettings(none);
                }}
                className="text-xs text-gray-500 hover:text-gray-700"
              >
                Deselect All
              </button>
            </div>
          </div>
          {pages?.map(page => {
            const settings = pageSettings?.[page?.id] || { included: true, priority: '0.8', changefreq: 'weekly' };
            const slug = page?.slug === '/' || page?.slug === 'home' ? '/' : `/${page?.slug}`;
            return (
              <div
                key={page?.id}
                className={`border rounded-lg p-3 transition-colors ${settings?.included ? 'border-green-200 bg-green-50' : 'border-gray-200 bg-gray-50 opacity-60'}`}
              >
                <div className="flex items-center gap-3">
                  <input
                    type="checkbox"
                    checked={settings?.included !== false}
                    onChange={() => togglePageIncluded(page?.id)}
                    className="w-4 h-4 text-green-600 rounded"
                  />
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2">
                      <span className="text-sm font-medium text-gray-900 truncate">{page?.title}</span>
                      <span className="text-xs text-gray-500 truncate hidden sm:block">{slug}</span>
                    </div>
                  </div>
                  <div className="flex items-center gap-2 flex-shrink-0">
                    <select
                      value={settings?.priority}
                      onChange={e => updatePageSetting(page?.id, 'priority', e?.target?.value)}
                      disabled={!settings?.included}
                      className="text-xs border border-gray-300 rounded px-2 py-1 bg-white focus:outline-none focus:ring-1 focus:ring-green-500"
                    >
                      {priorityOptions?.map(p => (
                        <option key={p} value={p}>Priority: {p}</option>
                      ))}
                    </select>
                    <select
                      value={settings?.changefreq}
                      onChange={e => updatePageSetting(page?.id, 'changefreq', e?.target?.value)}
                      disabled={!settings?.included}
                      className="text-xs border border-gray-300 rounded px-2 py-1 bg-white focus:outline-none focus:ring-1 focus:ring-green-500 hidden sm:block"
                    >
                      {changefreqOptions?.map(f => (
                        <option key={f} value={f}>{f}</option>
                      ))}
                    </select>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}
      {/* XML Preview */}
      {showPreview && sitemapXml && (
        <div className="border border-green-200 rounded-lg overflow-hidden">
          <div className="flex items-center justify-between bg-green-50 px-4 py-3 border-b border-green-200">
            <div className="flex items-center gap-2">
              <Icon name="Code" size={16} className="text-green-600" />
              <span className="text-sm font-semibold text-green-800">sitemap.xml Preview</span>
              <span className="text-xs bg-green-200 text-green-700 px-2 py-0.5 rounded-full">{includedCount} URLs</span>
            </div>
            <div className="flex items-center gap-2">
              <button
                onClick={handleCopy}
                className="flex items-center gap-1 text-xs text-green-700 hover:text-green-900 px-2 py-1 rounded hover:bg-green-100 transition-colors"
              >
                <Icon name={copied ? 'Check' : 'Copy'} size={14} />
                {copied ? 'Copied!' : 'Copy'}
              </button>
              <button
                onClick={handleDownload}
                className="flex items-center gap-1 text-xs text-green-700 hover:text-green-900 px-2 py-1 rounded hover:bg-green-100 transition-colors"
              >
                <Icon name="Download" size={14} />
                Download
              </button>
            </div>
          </div>
          <pre className="p-4 text-xs text-gray-700 bg-gray-900 text-green-400 overflow-x-auto max-h-64 font-mono leading-relaxed">
            {sitemapXml}
          </pre>
        </div>
      )}
      {!showPreview && (
        <div className="border-2 border-dashed border-gray-200 rounded-lg p-6 text-center">
          <Icon name="Map" size={32} className="text-gray-300 mx-auto mb-2" />
          <p className="text-sm text-gray-500">Click <strong>Generate Sitemap</strong> to create your sitemap.xml</p>
          <p className="text-xs text-gray-400 mt-1">The sitemap will include all {pages?.length} published pages with your configured settings</p>
        </div>
      )}
    </div>
  );
};

export default SitemapGenerator;
